import { Injectable } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { AuthenticationService } from '@core/services/auth.service';
import { filter } from 'rxjs/operators';
import { driver } from 'driver.js';
import type { AllowedButtons, DriveStep, PopoverDOM } from 'driver.js';

type TourStep = {
  id: string;
  title: string;
  description: string;
  adminOnly?: boolean;
  requiresInteraction?: boolean;
  disableContinue?: boolean;
  advanceOnRoute?: string;
  selector?: string;
  route?: string[];
  queryParams?: Params;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  nextText?: string;
  prevText?: string;
  helperText?: string;
  popoverClass?: string;
  onBeforeShow?: () => Promise<void> | void;
};

type MobileStepCopy = Partial<Pick<TourStep, 'title' | 'description' | 'helperText'>>;

type DockStepItem = {
  id: string;
  index: number;
  number: number;
  title: string;
  isCompleted: boolean;
  isCurrent: boolean;
  canNavigate: boolean;
};

type VisibleStepMeta = {
  id: string;
  title: string;
  actualIndex: number;
  visibleIndex: number;
};

type ConnectionsSetupStage =
  | 'overview-create'
  | 'select-type'
  | 'credentials'
  | 'connections-list';

@Injectable({
  providedIn: 'root',
})
export class OnboardingTourService {
  private static readonly MOBILE_MAX_WIDTH = 768;
  private readonly popoverButtons: AllowedButtons[] = [
    'previous',
    'next',
    'close',
  ];
  private readonly nextCloseButtons: AllowedButtons[] = ['next', 'close'];
  private readonly autoStartPrefix = 'ardia_onboarding_autostarted_';
  private readonly mergedIntoPreviousStepIds = new Set<string>([
    'settings-company-integral',
  ]);
  private readonly chatConnectionsHighlightClass =
    'onboarding-chat-connections-highlight';
  private readonly mobileStepCopies: Record<string, MobileStepCopy> = {
    welcome: {
      title: '¡Bienvenido a ARDIA!',
      description:
        'Conectamos la IA con tu ERP para darte una visión estratégica real.',
    },
    'home-overview': {
      title: 'Tu centro de mando',
      description: 'Accede a diferentes menús desde aquí.',
    },
    'settings-connections-overview': {
      title: 'Conecta tus datos',
      description: 'Vincula tu ERP para que la IA use tus datos reales.',
    },
    'company-data': {
      title: 'Datos Corporativos',
      description:
        'Consulta rápida de tus datos maestros y objetivos estratégicos.',
      helperText: '',
    },
    'settings-company-integral': {
      title: 'Actualiza tus datos',
      description: 'Estos datos dan contexto clave a tu IA.',
      helperText: '',
    },
    'agents-overview': {
      title: 'Conoce a tus agentes',
      description:
        'Cada uno está alimentado con datos y contextos específicos de su área.',
    },
    'first-query': {
      title: 'Tu primera consulta',
      description:
        '¡Todo listo! Pregunta: "¿Cuáles son mis productos más vendidos?".',
      helperText: '',
    },
  };
  private readonly baseSteps: TourStep[] = [
    {
      id: 'welcome',
      route: ['/dashboard/home'],
      selector: '[data-onboarding="home-hero"]',
      title: 'Bienvenida',
      description:
        'ARDIA conecta la IA con los datos reales de tu empresa para ayudarte a tomar decisiones con contexto y menos fricción.',
      side: 'bottom',
      align: 'center',
      nextText: 'Empezar configuración',
    },
    {
      id: 'home-overview',
      route: ['/dashboard/home'],
      selector: '[data-onboarding="sidebar-home"]',
      title: 'Tu centro de mando',
      description:
        'Desde aquí accederás a tus agentes, conexiones y la configuración de tu empresa.',
      popoverClass:
        'ardia-onboarding-popover ardia-onboarding-popover--sidebar',
      side: 'right',
      align: 'start',
      nextText: 'Ir a Conexiones',
      prevText: 'Volver',
      onBeforeShow: async () => {
        await this.ensureDashboardSidebarVisible();
        await this.scrollSelectorIntoView('[data-onboarding="sidebar-home"]', 120);
      },
    },
    {
      id: 'settings-connections-overview',
      route: ['/dashboard/settings', 'connections'],
      selector: '[data-onboarding="settings-connections-overview"]',
      title: 'Conecta tus datos',
      description:
        'El primer paso es vincular tu ERP (A3ERP, Mirai, etc.). Sin esto, la IA solo responderá de forma genérica.',
      popoverClass:
        'ardia-onboarding-popover ardia-onboarding-popover--settings-profile',
      side: 'bottom',
      align: 'center',
      nextText: 'Siguiente',
      prevText: 'Anterior',
      onBeforeShow: async () => {
        await this.ensureMobileSidebarHidden();
        await this.scrollSelectorIntoView(
          '[data-onboarding="settings-connections-overview"]',
          120,
        );
      },
    },
    {
      id: 'company-data',
      route: ['/dashboard/knowledge'],
      queryParams: {},
      selector: '[data-onboarding="knowledge-company-overview"]',
      title: 'Consulta tu Identidad Corporativa',
      description:
        'Aquí visualizas de forma rápida los datos de tu empresa. Al continuar, podrás editarlos si lo deseas dentro de este mismo paso.',
      helperText:
        'Nota: Este apartado es únicamente para lectura y consulta de la información actual',
      requiresInteraction: true,
      advanceOnRoute: '/dashboard/settings',
      popoverClass: 'ardia-onboarding-popover ardia-onboarding-popover--knowledge',
      side: 'top',
      align: 'center',
      nextText: 'Continuar',
      prevText: 'Anterior',
      onBeforeShow: async () => {
        await this.scrollSelectorIntoView(
          '[data-onboarding="knowledge-company-overview"]',
          150,
        );
      },
    },
    {
      id: 'settings-company-integral',
      route: ['/dashboard/settings', 'company'],
      queryParams: {},
      adminOnly: true,
      selector: '[data-onboarding="company-data-form"]',
      title: 'Edita el perfil de tu empresa',
      description:
        'Desde aquí (Configuración / Mi empresa) puedes modificar todos tus datos.',
      helperText:
        'Recuerda: cuanto más completa esté esta sección, de más información se alimentarán tus agentes para ayudarte.',
      popoverClass: 'ardia-onboarding-popover ardia-onboarding-popover--settings-company',
      side: 'left',
      align: 'start',
      nextText: 'Continuar',
      prevText: 'Anterior',
      onBeforeShow: async () => {
        await this.ensureCompanyProgressExpanded();
        await this.scrollSelectorIntoView('[data-onboarding="company-save-button"]', 180);
      },
    },
    {
      id: 'agents-overview',
      route: ['/dashboard/agents'],
      queryParams: {},
      selector: '[data-onboarding="agents-overview"]',
      title: 'Conoce a tus agentes',
      description:
        'Aquí verás a todos tus agentes disponibles. Cada uno está alimentado con datos y contextos específicos de su área (Ventas, Finanzas, RRHH) para darte siempre la respuesta más óptima y precisa.',
      popoverClass:
        'ardia-onboarding-popover ardia-onboarding-popover--settings-profile ardia-onboarding-popover--agents-overview',
      side: 'bottom',
      align: 'center',
      nextText: 'Continuar',
      prevText: 'Anterior',
      onBeforeShow: async () => {
        await this.scrollSelectorIntoView('[data-onboarding="agents-overview"]', 120);
      },
    },
    {
      id: 'first-query',
      route: ['/dashboard/chat'],
      selector: '[data-onboarding="chat-first-query"]',
      title: 'Tu primera consulta',
      description:
        'Ya puedes lanzar tu primera pregunta sobre facturación, stock o cualquier indicador del negocio. Cuando quieras, podrás repetir este recorrido desde el Centro de Ayuda.',
      helperText:
        'Recuerda que para obtener respuestas basadas en tus datos reales, debes tener tu ERP conectado y haber seleccionado la base de datos correspondiente.',
      popoverClass:
        'ardia-onboarding-popover ardia-onboarding-popover--chat-finish',
      side: 'top',
      align: 'center',
      nextText: 'Finalizar recorrido',
      prevText: 'Anterior',
      onBeforeShow: async () => {
        await this.scrollSelectorIntoView('[data-onboarding="chat-first-query"]', 120);
        await this.ensureChatConnectionsDropdownVisible();
        await this.nudgeSelectorScrollDown('[data-onboarding="chat-first-query"]', 80);
      },
    },
  ];

  private driverObj: ReturnType<typeof driver> | null = null;
  private currentStepIndex = 0;
  private isActive = false;
  private isInitialized = false;
  private showWelcomeOverlay = false;
  private mobilePopoverCollapsed = false;
  private activeContextClass: string | null = null;
  private interactionCleanup: (() => void) | null = null;
  private isStepTransitioning = false;
  private tourRunId = 0;
  private connectionsSetupStage: ConnectionsSetupStage = 'overview-create';

  constructor(
    private router: Router,
    private authService: AuthenticationService,
  ) {}

  init(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isActive) {
          void this.handleStepRouteProgression();
          return;
        }

        if (!this.isActive) {
          this.maybeAutoStart();
        }
      });

    this.maybeAutoStart();
  }

  restart(): void {
    this.clearAutoStartFlag();
    this.destroyDriver();
    this.isActive = false;
    this.currentStepIndex = 0;
    this.showWelcomeOverlay = false;
    void this.start(true);
  }

  isTourActive(): boolean {
    return this.isActive;
  }

  isWelcomeVisible(): boolean {
    return this.isActive && this.showWelcomeOverlay;
  }

  getWelcomeTitle(): string {
    if (this.isMobileOnboardingViewport()) {
      return '¡Bienvenido a ARDIA!';
    }

    return '¡Bienvenido a Ardia!';
  }

  getWelcomeDescription(): string {
    if (this.isMobileOnboardingViewport()) {
      return 'Conectamos la IA con tu ERP para darte una visión estratégica real.';
    }

    return 'ARDIA conecta la IA de última generación con los datos reales de tu ERP para darte una visión estratégica de tu negocio.';
  }

  getWelcomePrimaryActionLabel(): string {
    return 'Siguiente';
  }

  getCurrentStepNumber(): number {
    const visibleSteps = this.getVisibleStepsMeta();
    if (!visibleSteps.length) {
      return 0;
    }

    if (this.showWelcomeOverlay) {
      return 1;
    }

    const activeVisibleIndex = this.getVisibleIndexForActualStep(
      this.currentStepIndex,
    );
    return Math.max(1, activeVisibleIndex + 1);
  }

  getTotalSteps(): number {
    return this.getVisibleStepsMeta().length;
  }

  getCurrentStepLabel(): string {
    const visibleSteps = this.getVisibleStepsMeta();
    if (!visibleSteps.length) {
      return 'Onboarding';
    }

    const activeVisibleIndex = this.showWelcomeOverlay
      ? 0
      : this.getVisibleIndexForActualStep(this.currentStepIndex);

    const currentStep = this.getSteps()[this.currentStepIndex];
    if (currentStep?.id === 'settings-connections-overview') {
      return this.buildRuntimeStep(currentStep).title;
    }

    return visibleSteps[activeVisibleIndex]?.title ?? 'Onboarding';
  }

  getProgressPercentage(): number {
    return (this.getCurrentStepNumber() / this.getTotalSteps()) * 100;
  }

  getProgressPercentageLabel(): string {
    return `${Math.round(this.getProgressPercentage())}%`;
  }

  getDockSteps(): DockStepItem[] {
    const visibleSteps = this.getVisibleStepsMeta();
    const activeVisibleIndex = this.showWelcomeOverlay
      ? 0
      : this.getVisibleIndexForActualStep(this.currentStepIndex);

    return visibleSteps.map((step) => ({
      id: step.id,
      index: step.visibleIndex,
      number: step.visibleIndex + 1,
      title: step.title,
      isCompleted: step.visibleIndex < activeVisibleIndex,
      isCurrent: step.visibleIndex === activeVisibleIndex,
      canNavigate: step.visibleIndex <= activeVisibleIndex,
    }));
  }

  startFromWelcome(): void {
    if (!this.isActive) {
      return;
    }

    this.showWelcomeOverlay = false;

    if (this.getTotalSteps() <= 1) {
      this.finish(true);
      return;
    }

    void this.showStep(1);
  }

  skipCurrentStep(): void {
    if (!this.isActive) {
      return;
    }

    if (this.showWelcomeOverlay) {
      this.startFromWelcome();
      return;
    }

    void this.goToNextStep();
  }

  previousStep(): void {
    if (!this.isActive || this.showWelcomeOverlay) {
      return;
    }

    void this.goToPreviousStep();
  }

  jumpToStepFromDock(stepIndex: number): void {
    if (!this.isActive) {
      return;
    }

    const visibleSteps = this.getVisibleStepsMeta();
    if (stepIndex < 0 || stepIndex >= visibleSteps.length) {
      return;
    }

    const activeVisibleIndex = this.showWelcomeOverlay
      ? 0
      : this.getVisibleIndexForActualStep(this.currentStepIndex);
    if (stepIndex > activeVisibleIndex) {
      return;
    }

    if (stepIndex === activeVisibleIndex) {
      return;
    }

    const targetVisibleStep = visibleSteps[stepIndex];
    if (!targetVisibleStep) {
      return;
    }

    if (stepIndex === 0) {
      this.destroyDriver();
      this.currentStepIndex = 0;
      this.clearStepContext();
      void this.navigateIfNeeded(this.getSteps()[0]).then(() => {
        if (!this.isActive) {
          return;
        }

        this.showWelcomeOverlay = true;
      });
      return;
    }

    void this.showStep(targetVisibleStep.actualIndex);
  }

  finishTour(): void {
    this.finish(true);
  }

  continueTour(): void {
    if (!this.isActive) {
      return;
    }

    if (this.showWelcomeOverlay) {
      this.startFromWelcome();
      return;
    }

    if (this.isContinueDisabled()) {
      return;
    }

    const nextStep = this.currentStepIndex + 1;
    if (nextStep >= this.getSteps().length) {
      this.finish(true);
      return;
    }

    this.destroyDriver();
    void this.showStep(nextStep);
  }

  notifyConnectionsCreateStarted(): void {
    if (!this.isAtConnectionsSetupStep()) {
      return;
    }

    if (this.connectionsSetupStage !== 'overview-create') {
      return;
    }

    this.connectionsSetupStage = 'select-type';
    this.deferRefreshCurrentStep();
  }

  notifyConnectionTypeSelected(): void {
    if (!this.isAtConnectionsSetupStep()) {
      return;
    }

    if (this.connectionsSetupStage !== 'select-type') {
      return;
    }

    this.connectionsSetupStage = 'credentials';
    this.refreshCurrentStep();
  }

  notifyConnectionCreated(): void {
    if (!this.isAtConnectionsSetupStep()) {
      return;
    }

    if (this.connectionsSetupStage !== 'credentials') {
      return;
    }

    this.connectionsSetupStage = 'connections-list';
    this.refreshCurrentStep();
  }

  getContinueLabel(): string {
    const steps = this.getSteps();
    const step = steps[this.currentStepIndex];

    if (!step) {
      return 'Continuar';
    }

    return step.nextText ?? 'Continuar';
  }

  getDockContinueLabel(): string {
    if (this.showWelcomeOverlay) {
      return 'Continuar onboarding';
    }

    return this.isLastStep()
      ? 'Finalizar onboarding'
      : 'Continuar onboarding';
  }

  shouldShowDockContinueButton(): boolean {
    if (!this.isActive) {
      return false;
    }

    if (this.showWelcomeOverlay) {
      return true;
    }

    const step = this.getSteps()[this.currentStepIndex];
    if (step?.id === 'settings-connections-overview') {
      return this.connectionsSetupStage === 'connections-list';
    }

    return true;
  }

  isLastStep(): boolean {
    if (this.showWelcomeOverlay) {
      return false;
    }

    return this.currentStepIndex >= this.getSteps().length - 1;
  }

  continueConnectionsWithoutSetup(): void {
    if (!this.isAtConnectionsSetupStep()) {
      return;
    }

    if (this.connectionsSetupStage !== 'credentials') {
      return;
    }

    this.connectionsSetupStage = 'connections-list';
    void this.ensureConnectionsListView().then(() => {
      this.refreshCurrentStep();
    });
  }

  isConnectionTypeSelectionStageActive(): boolean {
    if (!this.isActive || this.showWelcomeOverlay) {
      return false;
    }

    const step = this.getSteps()[this.currentStepIndex];
    return (
      step?.id === 'settings-connections-overview' &&
      this.connectionsSetupStage === 'select-type'
    );
  }

  private isAtConnectionsSetupStep(): boolean {
    if (!this.isActive || this.showWelcomeOverlay) {
      return false;
    }

    const step = this.getSteps()[this.currentStepIndex];
    return step?.id === 'settings-connections-overview';
  }

  private refreshCurrentStep(): void {
    if (!this.isAtConnectionsSetupStep()) {
      return;
    }

    if (this.isStepTransitioning) {
      return;
    }

    this.destroyDriver();
    void this.showStep(this.currentStepIndex);
  }

  private deferRefreshCurrentStep(): void {
    window.setTimeout(() => {
      this.refreshCurrentStep();
    }, 0);
  }

  private buildRuntimeStep(step: TourStep): TourStep {
    if (step.id !== 'settings-connections-overview') {
      return step;
    }

    if (this.connectionsSetupStage === 'overview-create') {
      return {
        ...step,
        selector: '[data-onboarding="settings-connections-create-button"]',
        title: 'Conecta tus datos',
        description: 'Empieza creando una conexión nueva para vincular tu ERP.',
        popoverClass:
          'ardia-onboarding-popover ardia-onboarding-popover--settings-profile',
        onBeforeShow: async () => {
          await this.ensureMobileSidebarHidden();
          await this.ensureConnectionsListView();
          await this.scrollSelectorIntoView(
            '[data-onboarding="settings-connections-create-button"]',
            140,
          );
        },
      };
    }

    if (this.connectionsSetupStage === 'select-type') {
      return {
        ...step,
        selector: '[data-onboarding="settings-connection-create"]',
        title: 'Selecciona tipo de conexión',
        description:
          'Selecciona el tipo de conexión que quieres configurar para avanzar.',
        popoverClass:
          'ardia-onboarding-popover ardia-onboarding-popover--connection-create',
        onBeforeShow: async () => {
          await this.ensureCreateConnectionView();
          await this.scrollSelectorIntoView(
            '[data-onboarding="settings-connection-create"]',
            140,
          );
        },
      };
    }

    if (this.connectionsSetupStage === 'credentials') {
      return {
        ...step,
        selector: '[data-onboarding="settings-connection-create"]',
        title: 'Introducir las credenciales técnicas',
        description:
          'Importante: primero dale a "Probar conexión" y luego a "Crear conexión".',
        popoverClass:
          'ardia-onboarding-popover ardia-onboarding-popover--connection-create',
        onBeforeShow: async () => {
          await this.ensureCreateConnectionView();
          await this.scrollSelectorIntoView(
            '[data-onboarding="settings-connection-create"]',
            140,
          );
        },
      };
    }

    return {
      ...step,
      selector: '[data-onboarding="settings-connections-overview"]',
      title: 'Desde aquí ves las conexiones activas',
      description:
        'Cuando quieras, podrás volver aquí para revisar el estado de tus conexiones.',
      popoverClass:
        'ardia-onboarding-popover ardia-onboarding-popover--settings-profile',
      onBeforeShow: async () => {
        await this.ensureConnectionsListView();
        await this.scrollSelectorIntoView(
          '[data-onboarding="settings-connections-overview"]',
          120,
        );
      },
    };
  }

  private isMergedIntoPreviousStep(stepId: string): boolean {
    return this.mergedIntoPreviousStepIds.has(stepId);
  }

  private getVisibleStepsMeta(): VisibleStepMeta[] {
    const steps = this.getSteps();
    const visibleSteps: VisibleStepMeta[] = [];
    let visibleIndex = -1;

    for (let actualIndex = 0; actualIndex < steps.length; actualIndex += 1) {
      const step = steps[actualIndex];
      if (this.isMergedIntoPreviousStep(step.id)) {
        continue;
      }

      visibleIndex += 1;
      visibleSteps.push({
        id: step.id,
        title: step.title,
        actualIndex,
        visibleIndex,
      });
    }

    return visibleSteps;
  }

  private getVisibleIndexForActualStep(actualIndex: number): number {
    const steps = this.getSteps();
    if (actualIndex <= 0) {
      return 0;
    }

    let visibleIndex = -1;
    for (let i = 0; i <= actualIndex && i < steps.length; i += 1) {
      if (!this.isMergedIntoPreviousStep(steps[i].id)) {
        visibleIndex += 1;
      }
    }

    return Math.max(0, visibleIndex);
  }

  isContinueDisabled(): boolean {
    if (this.showWelcomeOverlay) {
      return false;
    }

    const step = this.getSteps()[this.currentStepIndex];
    if (step?.id === 'settings-connections-overview') {
      return this.connectionsSetupStage !== 'connections-list';
    }

    return Boolean(step?.disableContinue);
  }

  isPreviousDisabled(): boolean {
    return !this.isActive || this.showWelcomeOverlay;
  }

  private maybeAutoStart(): void {
    if (!this.isDashboardEligible()) {
      return;
    }

    if (!this.shouldShowOnboarding()) {
      return;
    }

    // El backend ya es la fuente de verdad. Si show_onboarding viene en true,
    // no dejamos que una marca vieja de session bloquee el autostart.
    this.clearAutoStartFlag();
    this.markAutoStartedThisSession();
    void this.start(false);
  }

  private async start(forceRestart: boolean): Promise<void> {
    if (this.isActive && !forceRestart) {
      return;
    }

    try {
      const runId = this.startNewRun();
      this.destroyDriver();
      this.isActive = true;
      this.isStepTransitioning = false;
      this.currentStepIndex = 0;
      this.showWelcomeOverlay = false;

      const firstStep = this.getSteps()[0];
      if (firstStep) {
        await this.navigateIfNeeded(firstStep);
        if (!this.isRunActive(runId)) {
          return;
        }
      }

      this.showWelcomeOverlay = true;
    } catch (error) {
      console.error('[OnboardingTour] Error starting tour:', error);
      this.finish(false);
    }
  }

  private async showStep(index: number): Promise<void> {
    if (this.isStepTransitioning) {
      return;
    }

    const runId = this.tourRunId;
    if (!this.isRunActive(runId)) {
      return;
    }

    this.isStepTransitioning = true;

    try {
      const steps = this.getSteps();
      const step = steps[index];
      if (!step) {
        this.finish(true);
        return;
      }

      const isSameStep = this.currentStepIndex === index;
      if (
        step.id === 'settings-connections-overview' &&
        !isSameStep
      ) {
        this.connectionsSetupStage = 'overview-create';
      }

      this.currentStepIndex = index;
      await this.navigateIfNeeded(step);
      if (!this.isRunActive(runId)) {
        return;
      }

      const runtimeStep = this.buildRuntimeStep(step);
      this.applyStepContext(runtimeStep);

      if (runtimeStep.onBeforeShow) {
        await runtimeStep.onBeforeShow();
        if (!this.isRunActive(runId)) {
          return;
        }
      }

      const element = runtimeStep.selector
        ? await this.waitForElement(runtimeStep.selector)
        : null;
      if (!this.isRunActive(runId)) {
        return;
      }

      this.setupInteractionProgression(runtimeStep, element);

      const isLastStep = index === steps.length - 1;
      const showNextInPopover =
        runtimeStep.id !== 'settings-connections-overview' ||
        this.connectionsSetupStage === 'connections-list';
      this.destroyDriver();
      if (!this.isRunActive(runId)) {
        return;
      }

      this.mobilePopoverCollapsed = false;
      this.showWelcomeOverlay = false;
      const stepConfig: DriveStep = {
        ...(element ? { element } : {}),
        popover: {
          title: runtimeStep.id === 'company-data' ? '' : runtimeStep.title,
          description: runtimeStep.description,
          side: runtimeStep.side ?? 'bottom',
          align: runtimeStep.align ?? 'center',
          popoverClass: runtimeStep.popoverClass,
          showButtons: showNextInPopover ? this.nextCloseButtons : ['close'],
          disableButtons: [],
          nextBtnText: isLastStep ? 'Finalizar onboarding' : 'Siguiente',
          prevBtnText: runtimeStep.prevText ?? 'Anterior',
          doneBtnText: 'Finalizar onboarding',
          onPopoverRender: (popover) => {
            this.decoratePopover(runtimeStep, popover);
          },
          onNextClick: () => {
            void this.goToNextStep();
          },
          onPrevClick: () => {
            void this.goToPreviousStep();
          },
          onCloseClick: () => {
            this.finish(true);
          },
        },
      };

      this.driverObj = driver({
        animate: true,
        allowClose: true,
        overlayOpacity: 0.56,
        smoothScroll: true,
        stagePadding: 10,
        popoverOffset: 20,
        popoverClass: 'ardia-onboarding-popover',
        steps: [stepConfig],
        showButtons: this.popoverButtons,
      });
      this.driverObj.drive();
    } finally {
      if (this.tourRunId === runId) {
        this.isStepTransitioning = false;
      }
    }
  }

  private async goToNextStep(): Promise<void> {
    if (this.isStepTransitioning) {
      return;
    }

    const steps = this.getSteps();
    const nextStep = this.currentStepIndex + 1;
    if (nextStep >= steps.length) {
      this.finish(true);
      return;
    }

    await this.showStep(nextStep);
  }

  private async goToPreviousStep(): Promise<void> {
    if (this.isStepTransitioning) {
      return;
    }

    const prevStep = this.currentStepIndex - 1;
    if (prevStep < 0) {
      return;
    }

    if (prevStep === 0) {
      this.destroyDriver();
      this.currentStepIndex = 0;
      this.showWelcomeOverlay = true;
      return;
    }

    await this.showStep(prevStep);
  }

  private async handleStepRouteProgression(): Promise<void> {
    if (this.showWelcomeOverlay) {
      return;
    }

    const step = this.getSteps()[this.currentStepIndex];
    if (!step?.requiresInteraction || !step.advanceOnRoute) {
      return;
    }

    if (!window.location.pathname.startsWith(step.advanceOnRoute)) {
      return;
    }

    await this.delay(90);

    if (this.getSteps()[this.currentStepIndex]?.id !== step.id) {
      return;
    }

    await this.goToNextStep();
  }

  private setupInteractionProgression(
    step: TourStep,
    element: Element | null,
  ): void {
    this.clearInteractionProgression();

    if (!step.requiresInteraction || !(element instanceof HTMLElement)) {
      return;
    }

    const handleInteraction = (): void => {
      window.setTimeout(() => {
        if (!this.isActive || this.showWelcomeOverlay) {
          return;
        }

        if (this.getSteps()[this.currentStepIndex]?.id !== step.id) {
          return;
        }

        void this.goToNextStep();
      }, 80);
    };

    element.addEventListener('click', handleInteraction, { once: true });
    this.interactionCleanup = () => {
      element.removeEventListener('click', handleInteraction);
    };
  }

  private clearInteractionProgression(): void {
    this.interactionCleanup?.();
    this.interactionCleanup = null;
  }

  private async navigateIfNeeded(step: TourStep): Promise<void> {
    if (!step.route) {
      return;
    }

    const targetUrl = this.router.createUrlTree(step.route, {
      queryParams: step.queryParams,
    });
    const targetSerialized = this.router.serializeUrl(targetUrl);
    const currentPath = window.location.pathname;
    const currentQuery = window.location.search;
    const currentSerialized = `${currentPath}${currentQuery}`;

    if (currentSerialized === targetSerialized) {
      await this.delay(120);
      return;
    }

    await this.router.navigate(step.route, {
      queryParams: step.queryParams,
    });
    await this.delay(220);
  }

  private async waitForElement(selector: string): Promise<Element | null> {
    const maxAttempts = 30;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }

      await this.delay(120);
    }

    console.warn(
      `[OnboardingTour] Element not found for selector: ${selector}`,
    );
    return null;
  }

  private async scrollSelectorIntoView(
    selector: string,
    offset = 0,
  ): Promise<void> {
    const element = await this.waitForElement(selector);
    if (!element) {
      return;
    }

    const scrollParent = this.getScrollableParent(element);
    const elementRect = element.getBoundingClientRect();

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const targetTop =
        scrollParent.scrollTop + (elementRect.top - parentRect.top) - offset;

      scrollParent.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });
    } else {
      const targetTop = window.scrollY + elementRect.top - offset;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });
    }

    await this.delay(180);
  }

  private async scrollContainerToTop(selector: string): Promise<void> {
    const element = await this.waitForElement(selector);
    if (!(element instanceof HTMLElement)) {
      return;
    }

    element.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    await this.delay(180);
  }

  private async nudgeSelectorScrollDown(
    selector: string,
    delta = 80,
  ): Promise<void> {
    const element = await this.waitForElement(selector);
    if (!element) {
      return;
    }

    const scrollParent = this.getScrollableParent(element);
    if (scrollParent) {
      scrollParent.scrollBy({
        top: delta,
        behavior: 'smooth',
      });
    } else {
      window.scrollBy({
        top: delta,
        behavior: 'smooth',
      });
    }

    await this.delay(140);
  }

  private async ensureDashboardSidebarVisible(): Promise<void> {
    const sidebarItem = document.querySelector('[data-onboarding="sidebar-home"]');
    if (sidebarItem) {
      return;
    }

    const menuToggle = document.querySelector(
      '[data-onboarding="dashboard-menu-toggle"]',
    );

    if (menuToggle instanceof HTMLElement) {
      menuToggle.click();
      await this.delay(220);
    }
  }

  private async ensureMobileSidebarHidden(): Promise<void> {
    if (!this.isMobileOnboardingViewport()) {
      return;
    }

    const sidebarItem = document.querySelector('[data-onboarding="sidebar-home"]');
    if (!sidebarItem) {
      return;
    }

    const menuToggle = document.querySelector(
      '[data-onboarding="dashboard-menu-toggle"]',
    );

    if (menuToggle instanceof HTMLElement) {
      menuToggle.click();
      await this.delay(220);
    }
  }

  private async ensureCreateConnectionView(): Promise<void> {
    const form = document.querySelector('[data-onboarding="settings-connection-create"]');
    if (form) {
      return;
    }

    const createButton = document.querySelector(
      '[data-onboarding="settings-connections-create-button"]',
    );
    if (createButton instanceof HTMLElement) {
      createButton.click();
      await this.delay(220);
    }
  }

  private async ensureConnectionsListView(): Promise<void> {
    const list = document.querySelector(
      '[data-onboarding="settings-connections-overview"]',
    );
    const form = document.querySelector('[data-onboarding="settings-connection-create"]');

    if (list && !form) {
      return;
    }

    const backButton = document.querySelector(
      '[data-onboarding="settings-connections-back-to-list"]',
    );
    if (backButton instanceof HTMLElement) {
      backButton.click();
      await this.delay(220);
    }
  }

  private async ensureConnectionTypeDropdownOpen(): Promise<void> {
    const firstOption = document.querySelector(
      '[data-onboarding="settings-connection-type-option"]',
    );
    if (firstOption) {
      return;
    }

    const trigger = document.querySelector(
      '[data-onboarding="settings-connection-type-trigger"]',
    );
    if (trigger instanceof HTMLElement) {
      trigger.click();
      await this.delay(180);
    }
  }

  private async ensureCompanyProgressExpanded(): Promise<void> {
    const saveButton = document.querySelector(
      '[data-onboarding="company-save-button"]',
    );
    if (saveButton) {
      return;
    }

    const expandButton = document.querySelector(
      'button[title="Expandir progreso del perfil"]',
    );

    if (expandButton instanceof HTMLElement) {
      expandButton.click();
      await this.delay(220);
    }
  }

  private async ensureChatConnectionsDropdownVisible(): Promise<void> {
    const trigger = await this.waitForVisibleElement(
      '[data-onboarding="chat-connections-selector-trigger"]',
    );
    if (!trigger) {
      document.body.classList.remove(this.chatConnectionsHighlightClass);
      return;
    }

    document.body.classList.add(this.chatConnectionsHighlightClass);

    const existingMenu = this.getVisibleElement(
      '[data-onboarding="chat-connections-selector-menu"]',
    );
    if (!existingMenu) {
      trigger.click();
      await this.delay(180);
    }

    const menu = await this.waitForVisibleElement(
      '[data-onboarding="chat-connections-selector-menu"]',
    );
    if (!menu) {
      return;
    }

    const hasSelectableConnections = menu.querySelectorAll('button').length > 0;
    const menuText = (menu.textContent || '').toLowerCase();
    const hasEmptyState = menuText.includes('sin conexiones disponibles');

    if (!hasSelectableConnections || hasEmptyState) {
      return;
    }
  }

  private getScrollableParent(element: Element): HTMLElement | null {
    let current = element.parentElement;

    while (current) {
      const styles = window.getComputedStyle(current);
      const overflowY = styles.overflowY;
      const isScrollable =
        /(auto|scroll|overlay)/.test(overflowY) &&
        current.scrollHeight > current.clientHeight;

      if (isScrollable) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private getHorizontalScrollableParent(element: Element): HTMLElement | null {
    let current = element.parentElement;

    while (current) {
      const styles = window.getComputedStyle(current);
      const overflowX = styles.overflowX;
      const isScrollable =
        /(auto|scroll|overlay)/.test(overflowX) &&
        current.scrollWidth > current.clientWidth;

      if (isScrollable) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private async waitForVisibleElement(
    selector: string,
  ): Promise<HTMLElement | null> {
    const maxAttempts = 25;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const element = this.getVisibleElement(selector);
      if (element) {
        return element;
      }
      await this.delay(120);
    }

    return null;
  }

  private getVisibleElement(selector: string): HTMLElement | null {
    const elements = Array.from(document.querySelectorAll(selector));
    for (const element of elements) {
      if (!(element instanceof HTMLElement)) {
        continue;
      }

      if (
        element.offsetParent !== null ||
        element.getClientRects().length > 0
      ) {
        return element;
      }
    }

    return null;
  }

  private finish(markCompleted: boolean): void {
    this.tourRunId += 1;
    this.isStepTransitioning = false;

    if (markCompleted) {
      this.completeOnboarding();
    }

    this.destroyDriver();
    this.isActive = false;
    this.currentStepIndex = 0;
    this.showWelcomeOverlay = false;
    this.clearStepContext();
  }

  private destroyDriver(): void {
    this.clearInteractionProgression();
    this.driverObj?.destroy();
    this.driverObj = null;
    this.mobilePopoverCollapsed = false;
  }

  private applyStepContext(step: TourStep): void {
    this.clearStepContext();

    if (
      step.id === 'settings-connections-overview' &&
      this.isMobileOnboardingViewport() &&
      (this.connectionsSetupStage === 'select-type' ||
        this.connectionsSetupStage === 'credentials')
    ) {
      this.activeContextClass = 'onboarding-connection-modal-mobile';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (
      step.id === 'settings-connections-overview' &&
      this.connectionsSetupStage === 'select-type'
    ) {
      this.activeContextClass = 'onboarding-connection-type-select';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (
      step.id === 'settings-connections-overview' &&
      this.connectionsSetupStage === 'credentials'
    ) {
      this.activeContextClass = 'onboarding-connection-credentials';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (step.id === 'home-overview') {
      this.activeContextClass = 'onboarding-dashboard-navigation';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (step.id === 'settings-profile') {
      this.activeContextClass = 'onboarding-settings-profile';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (step.id === 'settings-company-integral') {
      this.activeContextClass = 'onboarding-settings-company-edit';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (step.id === 'agents-overview') {
      this.activeContextClass = 'onboarding-agents-overview';
      document.body.classList.add(this.activeContextClass);
      return;
    }

    if (step.id === 'first-query') {
      this.activeContextClass = 'onboarding-chat-first-query';
      document.body.classList.add(this.activeContextClass);
    }
  }

  private clearStepContext(): void {
    document.body.classList.remove(this.chatConnectionsHighlightClass);

    if (!this.activeContextClass) {
      return;
    }

    document.body.classList.remove(this.activeContextClass);
    this.activeContextClass = null;
  }

  private decoratePopover(step: TourStep, popover: PopoverDOM): void {
    this.decorateMobileCollapseControl(popover);
    this.enforceMobileTopPlacement(step, popover);

    if (step.id === 'settings-connections-overview') {
      if (this.connectionsSetupStage === 'credentials') {
        popover.description.innerHTML =
          'Importante: primero dale a <span class="ardia-onboarding-popover__highlight">"Probar conexión"</span> y luego a <span class="ardia-onboarding-popover__highlight">"Crear conexión"</span>.';

        if (
          !popover.wrapper.querySelector(
            '.ardia-onboarding-popover__skip-connection',
          )
        ) {
          const skipButton = document.createElement('button');
          skipButton.type = 'button';
          skipButton.className = 'ardia-onboarding-popover__skip-connection';
          skipButton.textContent = 'Continuar sin llenar formulario';
          skipButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.continueConnectionsWithoutSetup();
          });
          popover.footer.parentElement?.insertBefore(skipButton, popover.footer);
        }
      }

      return;
    }

    if (step.id === 'company-data') {
      if (!popover.wrapper.querySelector('.ardia-onboarding-popover__icon')) {
        const icon = document.createElement('div');
        icon.className = 'ardia-onboarding-popover__icon ardia-onboarding-popover__icon--knowledge';
        icon.innerHTML =
          '<svg viewBox="0 0 24 24" class="w-5 h-5" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor"></circle><rect x="11" y="10" width="2" height="7" rx="1" fill="white"></rect><rect x="11" y="6.5" width="2" height="2" rx="1" fill="white"></rect></svg>';
        popover.wrapper.insertBefore(icon, popover.title);
      }

      if (
        step.helperText &&
        !popover.wrapper.querySelector('.ardia-onboarding-popover__note')
      ) {
        const note = document.createElement('div');
        note.className =
          'ardia-onboarding-popover__note ardia-onboarding-popover__note--knowledge';
        note.innerHTML =
          '<span class="ardia-onboarding-popover__note-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M19.14 12.94a7.43 7.43 0 0 0 .05-.94 7.43 7.43 0 0 0-.05-.94l2.03-1.58a.48.48 0 0 0 .11-.61l-1.92-3.32a.49.49 0 0 0-.58-.22l-2.39.96a7.2 7.2 0 0 0-1.63-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.48.41l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.49.49 0 0 0-.58.22L2.71 8.87a.48.48 0 0 0 .11.61l2.03 1.58a7.43 7.43 0 0 0-.05.94c0 .32.02.63.05.94l-2.03 1.58a.48.48 0 0 0-.11.61l1.92 3.32a.49.49 0 0 0 .58.22l2.39-.96c.51.4 1.05.71 1.63.94l.36 2.54a.48.48 0 0 0 .48.41h3.84a.48.48 0 0 0 .48-.41l.36-2.54c.58-.23 1.12-.54 1.63-.94l2.39.96a.49.49 0 0 0 .58-.22l1.92-3.32a.48.48 0 0 0-.11-.61l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z" fill="currentColor"/></svg></span><span>' +
          step.helperText +
          '</span>';
        popover.footer.parentElement?.insertBefore(note, popover.footer);
      }
    }

    if (step.id === 'settings-profile') {
      popover.description.innerHTML =
        'Tu perfil ayuda a personalizar el tono y el contexto de las respuestas. <span class="ardia-onboarding-popover__highlight">Completa Cargo, Departamento, LinkedIn y Teléfono</span> para que los agentes adapten mejor sus recomendaciones.';
      return;
    }

    if (step.id === 'settings-company-integral') {
      if (
        step.helperText &&
        !popover.wrapper.querySelector('.ardia-onboarding-popover__note')
      ) {
        const note = document.createElement('div');
        note.className = 'ardia-onboarding-popover__note';
        note.textContent = step.helperText;
        popover.footer.parentElement?.insertBefore(note, popover.footer);
      }
      return;
    }

    if (step.id === 'first-query') {
      if (
        step.helperText &&
        !popover.wrapper.querySelector('.ardia-onboarding-popover__note')
      ) {
        const note = document.createElement('div');
        note.className =
          'ardia-onboarding-popover__note ardia-onboarding-popover__note--chat-finish';
        note.textContent = step.helperText;
        popover.footer.parentElement?.insertBefore(note, popover.footer);
      }
    }
  }

  private enforceMobileTopPlacement(step: TourStep, popover: PopoverDOM): void {
    if (!this.isMobileOnboardingViewport()) {
      return;
    }

    const shouldStickTop =
      step.id === 'settings-company-integral' ||
      step.id === 'agents-overview';

    if (!shouldStickTop) {
      return;
    }

    const wrapper = popover.wrapper as HTMLElement;
    wrapper.style.setProperty('top', '5.25rem', 'important');
    wrapper.style.setProperty('bottom', 'auto', 'important');
    wrapper.style.setProperty('left', '50%', 'important');
    wrapper.style.setProperty('right', 'auto', 'important');
    wrapper.style.setProperty('transform', 'translateX(-50%)', 'important');
  }

  private decorateMobileCollapseControl(popover: PopoverDOM): void {
    if (!this.isMobileOnboardingViewport()) {
      return;
    }

    const wrapper = popover.wrapper as HTMLElement;
    let toggle = wrapper.querySelector(
      '.ardia-onboarding-popover__collapse-toggle',
    ) as HTMLButtonElement | null;

    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'ardia-onboarding-popover__collapse-toggle';
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.mobilePopoverCollapsed = !wrapper.classList.contains(
          'ardia-onboarding-popover--collapsed-mobile',
        );
        this.setMobilePopoverCollapsedState(wrapper, this.mobilePopoverCollapsed);
      });
      wrapper.appendChild(toggle);
    }

    this.setMobilePopoverCollapsedState(wrapper, this.mobilePopoverCollapsed);
  }

  private setMobilePopoverCollapsedState(
    wrapper: HTMLElement,
    collapsed: boolean,
  ): void {
    wrapper.classList.toggle('ardia-onboarding-popover--collapsed-mobile', collapsed);

    const toggle = wrapper.querySelector(
      '.ardia-onboarding-popover__collapse-toggle',
    ) as HTMLButtonElement | null;
    if (!toggle) {
      return;
    }

    toggle.textContent = '';
    toggle.setAttribute('data-symbol', collapsed ? '+' : '−');
    toggle.setAttribute('aria-label', collapsed ? 'Expandir paso' : 'Colapsar paso');
    toggle.classList.toggle('is-collapsed', collapsed);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  private isDashboardEligible(): boolean {
    const path = window.location.pathname;
    return path.startsWith('/dashboard') && !path.includes('/home-backoffice');
  }

  private getStorageKey(prefix: string): string {
    return `${prefix}${this.getUserIdentity()}`;
  }

  private getSteps(): TourStep[] {
    const steps = this.isAdminUser()
      ? this.baseSteps
      : this.baseSteps.filter((step) => !step.adminOnly);

    if (!this.isMobileOnboardingViewport()) {
      return steps;
    }

    return steps.map((step) => {
      const mobileCopy = this.mobileStepCopies[step.id];
      if (!mobileCopy) {
        return step;
      }

      return {
        ...step,
        ...mobileCopy,
      };
    });
  }

  private isMobileOnboardingViewport(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(
      `(max-width: ${OnboardingTourService.MOBILE_MAX_WIDTH}px)`,
    ).matches;
  }

  private getUserIdentity(): string {
    const rawUser =
      localStorage.getItem('user') ?? sessionStorage.getItem('user');

    if (!rawUser) {
      return 'anonymous';
    }

    try {
      const user = JSON.parse(rawUser);
      return String(user?.id ?? user?.email ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private isAdminUser(): boolean {
    const user = this.authService.getCurrentUser();

    if (!user) {
      return false;
    }

    try {
      const roles = Array.isArray(user?.roles) ? user.roles : [];
      return roles.some((role: { id?: number }) =>
        [1, 2].includes(role?.id ?? 0),
      );
    } catch {
      return false;
    }
  }

  private shouldShowOnboarding(): boolean {
    const user = this.authService.getCurrentUser();
    return user?.show_onboarding === true;
  }

  private completeOnboarding(): void {
    this.authService.updateCurrentUser({
      show_onboarding: false,
    });

    this.authService.completeOnboarding().subscribe({
      error: (error) => {
        console.warn(
          '[OnboardingTour] Failed to persist onboarding completion:',
          error,
        );
      },
    });
  }

  private wasAutoStartedThisSession(): boolean {
    return (
      sessionStorage.getItem(this.getStorageKey(this.autoStartPrefix)) === '1'
    );
  }

  private markAutoStartedThisSession(): void {
    sessionStorage.setItem(this.getStorageKey(this.autoStartPrefix), '1');
  }

  private clearAutoStartFlag(): void {
    sessionStorage.removeItem(this.getStorageKey(this.autoStartPrefix));
  }

  private startNewRun(): number {
    this.tourRunId += 1;
    return this.tourRunId;
  }

  private isRunActive(runId: number): boolean {
    return this.isActive && this.tourRunId === runId;
  }
}
